import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';
import { Download as DownloadIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import api from '../../utils/api';
import { FeedbackStats, ApplicationFeedback, FEEDBACK_REASON_LABELS, FeedbackReason } from '../../types/applicationFeedback.types';

const COLORS = {
  NOT_ENOUGH_TIME: '#FF6B6B',
  FOUND_OTHER_PLACEMENT: '#4ECDC4',
  TOO_DEMANDING: '#FFE66D',
  CHANGED_MIND: '#95E1D3',
  TECHNICAL_ISSUES: '#F38181',
  OTHER: '#AA96DA'
};

const FeedbackAnalytics: React.FC = () => {
  const [stats, setStats] = useState<FeedbackStats | null>(null);
  const [feedbackList, setFeedbackList] = useState<ApplicationFeedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [campaignFilter, setCampaignFilter] = useState<string>('');
  const [campaigns, setCampaigns] = useState<string[]>([]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {};
      if (campaignFilter) {
        params.campaignName = campaignFilter;
      }

      const [statsResponse, listResponse] = await Promise.all([
        api.get('/api/application-feedback/stats', { params }),
        api.get('/api/application-feedback/list', { params: { ...params, submitted: true, limit: 100 } })
      ]);

      setStats(statsResponse.data.stats);
      setFeedbackList(listResponse.data.data);

      // Extract unique campaigns
      const campaignSet = new Set(
        listResponse.data.data
          .map((f: ApplicationFeedback) => f.recruitmentCampaign)
          .filter(Boolean)
      );
      const uniqueCampaigns = Array.from(campaignSet) as string[];
      setCampaigns(uniqueCampaigns);

    } catch (err: any) {
      console.error('Error fetching feedback data:', err);
      setError(err.response?.data?.error || 'Failed to load feedback data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [campaignFilter]);

  const exportToCSV = () => {
    if (!feedbackList.length) return;

    const headers = ['Name', 'Email', 'Reason', 'Details', 'Would Reapply', 'Suggestions', 'Campaign', 'Submitted Date'];
    const rows = feedbackList.map(f => [
      f.counsellorName,
      f.counsellorEmail,
      f.reason ? FEEDBACK_REASON_LABELS[f.reason] : '',
      f.reasonText || '',
      f.wouldReapplyLater ? 'Yes' : 'No',
      f.suggestions || '',
      f.recruitmentCampaign || '',
      f.feedbackSubmittedDate ? new Date(f.feedbackSubmittedDate).toLocaleDateString() : ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `application-feedback-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getPieChartData = () => {
    if (!stats?.reasonBreakdown) return [];
    
    return Object.entries(stats.reasonBreakdown).map(([reason, count]) => ({
      name: FEEDBACK_REASON_LABELS[reason as FeedbackReason],
      value: count,
      color: COLORS[reason as FeedbackReason]
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!stats) {
    return <Alert severity="info">No feedback data available</Alert>;
  }

  const pieData = getPieChartData();

  return (
    <Box>
      {/* Header Controls */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 'bold' }}>
          Application Feedback Analytics
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Campaign</InputLabel>
            <Select
              value={campaignFilter}
              onChange={(e) => setCampaignFilter(e.target.value)}
              label="Campaign"
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            >
              <MenuItem value="">All Campaigns</MenuItem>
              {campaigns.map(campaign => (
                <MenuItem key={campaign} value={campaign}>{campaign}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            sx={{ color: 'rgba(255, 255, 255, 0.9)', borderColor: 'rgba(255, 255, 255, 0.3)' }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={exportToCSV}
            disabled={!feedbackList.length}
            sx={{ backgroundColor: 'rgba(99, 102, 241, 0.8)' }}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <CardContent>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }} gutterBottom variant="body2">
                Total Requests Sent
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.95)' }}>
                {stats.totalRequests}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <CardContent>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }} gutterBottom variant="body2">
                Responses Received
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.95)' }}>
                {stats.totalResponses}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <CardContent>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }} gutterBottom variant="body2">
                Response Rate
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'rgba(76, 175, 80, 0.95)' }}>
                {stats.responseRate}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <CardContent>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }} gutterBottom variant="body2">
                Would Reapply
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'rgba(33, 150, 243, 0.95)' }}>
                {stats.wouldReapplyLater.percentage}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                {stats.wouldReapplyLater.count} of {stats.totalResponses}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts and Details */}
      <Grid container spacing={3}>
        {/* Pie Chart */}
        <Grid item xs={12} md={6}>
          <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
                Reasons for Not Completing
              </Typography>
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }: any) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', textAlign: 'center', py: 4 }}>
                  No data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Reason Breakdown Table */}
        <Grid item xs={12} md={6}>
          <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
                Reason Breakdown
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', fontWeight: 'bold' }}>Reason</TableCell>
                      <TableCell align="right" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontWeight: 'bold' }}>Count</TableCell>
                      <TableCell align="right" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontWeight: 'bold' }}>%</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(stats.reasonBreakdown).map(([reason, count]) => {
                      const percentage = stats.totalResponses > 0 
                        ? ((count / stats.totalResponses) * 100).toFixed(1) 
                        : '0';
                      return (
                        <TableRow key={reason}>
                          <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                            {FEEDBACK_REASON_LABELS[reason as FeedbackReason]}
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                            {count}
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                            {percentage}%
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Detailed Feedback List */}
        <Grid item xs={12}>
          <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
                Recent Feedback ({feedbackList.length})
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', fontWeight: 'bold' }}>Name</TableCell>
                      <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', fontWeight: 'bold' }}>Reason</TableCell>
                      <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', fontWeight: 'bold' }}>Details</TableCell>
                      <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', fontWeight: 'bold' }}>Reapply?</TableCell>
                      <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', fontWeight: 'bold' }}>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {feedbackList.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ textAlign: 'center', color: 'rgba(255, 255, 255, 0.5)', py: 4 }}>
                          No feedback received yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      feedbackList.slice(0, 10).map((feedback) => (
                        <TableRow key={feedback._id}>
                          <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                            {feedback.counsellorName}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={feedback.reason ? FEEDBACK_REASON_LABELS[feedback.reason] : 'N/A'}
                              size="small"
                              sx={{
                                backgroundColor: feedback.reason ? `${COLORS[feedback.reason]}40` : 'rgba(158, 158, 158, 0.2)',
                                color: 'rgba(255, 255, 255, 0.9)',
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', maxWidth: 200 }}>
                            <Typography variant="body2" noWrap title={feedback.reasonText || ''}>
                              {feedback.reasonText || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={feedback.wouldReapplyLater ? 'Yes' : 'No'}
                              size="small"
                              sx={{
                                backgroundColor: feedback.wouldReapplyLater 
                                  ? 'rgba(76, 175, 80, 0.2)' 
                                  : 'rgba(244, 67, 54, 0.2)',
                                color: 'rgba(255, 255, 255, 0.9)',
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                            {feedback.feedbackSubmittedDate 
                              ? new Date(feedback.feedbackSubmittedDate).toLocaleDateString()
                              : 'N/A'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FeedbackAnalytics;
